import {
  type FaceKey,
  type FacePhoto,
  isRequiredFace,
  type ResolvedEditAreaDefinition,
} from '@repo/api-contracts';
import { compositeAssetUrls, isCompositeSlot } from '../lib/composite-asset';
import type { PhotoDraftOptions, PhotoDraftState } from './photo-draft-types';

interface PhotoDraftSliceOptions extends PhotoDraftOptions {
  readonly autoHydrate?: boolean;
  readonly allowSavedFaceRemoval?: boolean;
  readonly canEdit?: () => boolean;
}

const FACE_ORDER: readonly FaceKey[] = ['front', 'back', 'left', 'right'];
const slotKey = (colorOptionId: string, face: FaceKey): string => `${colorOptionId}:${face}`;
const photoOnly = (photo: FacePhoto): FacePhoto => ({
  photoKey: photo.photoKey,
  photoSize: { ...photo.photoSize },
});
/** 저장된 정의가 있으면 그 면들, 없으면(새 상품) 앞·뒤 — 뒷면은 선택이지만 기본으로 켜 두고 한 면만 인쇄하는 상품이 끈다. */
const initialFaces = (definition?: ResolvedEditAreaDefinition | null): readonly FaceKey[] =>
  FACE_ORDER.filter((face) => {
    if (isRequiredFace(face)) return true;
    return definition ? definition.faces[face] !== undefined : face === 'back';
  });
/** 저장된 정의가 쓰는 선택 면(뒤·좌·우) — 사진 전용 화면은 이 면을 끄지 못하게 잠근다. 정의가 없으면 잠글 면도 없다. */
const lockableFaces = (definition?: ResolvedEditAreaDefinition | null): readonly FaceKey[] =>
  definition ? initialFaces(definition).filter((face) => !isRequiredFace(face)) : [];

const initialColors = (ids: readonly string[], definition?: ResolvedEditAreaDefinition | null) =>
  ids.map((colorOptionId) => {
    const source = definition?.colors.find((color) => color.colorOptionId === colorOptionId);
    const faces: Partial<Record<FaceKey, FacePhoto>> = Object.fromEntries(
      FACE_ORDER.flatMap((face) => {
        const photo = source?.faces[face];
        return photo ? [[face, photoOnly(photo)]] : [];
      }),
    );
    return { colorOptionId, faces };
  });

/** 저장 키 → 표시 URL. 면 사진과 면 합성 에셋(마스크·음영·안내선) 키를 함께 담는다. */
const resolvedUrls = (definition?: ResolvedEditAreaDefinition | null): Record<string, string> =>
  Object.fromEntries([
    ...(definition?.colors ?? []).flatMap((color) =>
      Object.values(color.faces).map((photo) => [photo.photoKey, photo.photoUrl]),
    ),
    ...FACE_ORDER.flatMap((face) => {
      const composite = definition?.faces[face]?.composite;
      return composite ? compositeAssetUrls(composite) : [];
    }),
  ]);

/** 화면별 상태. 저장용 사진과 일시적인 전송 상태를 분리한다. */
export const createPhotoDraftSlice = (
  options: PhotoDraftSliceOptions,
  set: (patch: Partial<PhotoDraftState>) => void,
  get: () => PhotoDraftState,
): PhotoDraftState => {
  let appliedDefinition = options.definition;
  let latestDefinition = options.definition;
  const validSlot = (colorOptionId: string, face: FaceKey): boolean =>
    get().draft.activeFaces.includes(face) &&
    get().draft.colors.some((color) => color.colorOptionId === colorOptionId);
  const currentOperation = (colorOptionId: string, face: FaceKey, id: string): boolean =>
    validSlot(colorOptionId, face) && get().operations[slotKey(colorOptionId, face)]?.id === id;
  const canEdit = (): boolean => !get().locked && (options.canEdit?.() ?? true);
  const removeOperation = (key: string): PhotoDraftState['operations'] =>
    Object.fromEntries(Object.entries(get().operations).filter(([slot]) => slot !== key));
  const notifyChange = (): void => options.onPhotosChange?.(get().draft);

  return {
    locked: false,
    generation: 0,
    draft: {
      productId: options.productId,
      activeFaces: initialFaces(options.definition),
      colors: initialColors(options.colorOptionIds, options.definition),
    },
    dirty: false,
    notice: null,
    lockedFaces: options.allowSavedFaceRemoval ? [] : lockableFaces(options.definition),
    photoUrls: resolvedUrls(options.definition),
    operations: {},
    reconcile: (ids, definition) => {
      const state = get();
      latestDefinition = definition;
      const removed = state.draft.colors.some((color) => !ids.includes(color.colorOptionId));
      // 합성 작업은 색상과 무관한 면 공통 작업이라 색상 삭제로 취소하지 않는다.
      const operations = removed
        ? Object.fromEntries(
            Object.entries(state.operations).filter(
              ([key]) => isCompositeSlot(key) || ids.some((id) => key.startsWith(`${id}:`)),
            ),
          )
        : state.operations;
      const hydrate =
        options.autoHydrate !== false &&
        definition !== appliedDefinition &&
        JSON.stringify(definition) !== JSON.stringify(appliedDefinition) &&
        !state.dirty &&
        Object.keys(operations).length === 0;
      if (hydrate) appliedDefinition = definition;
      const sameIds =
        ids.length === state.draft.colors.length &&
        ids.every((id, index) => state.draft.colors[index]?.colorOptionId === id);
      const freshUrls = resolvedUrls(definition);
      const urlsChanged = Object.entries(freshUrls).some(
        ([key, value]) => state.photoUrls[key] !== value,
      );
      if (sameIds && !urlsChanged && !hydrate) return;
      const colors = hydrate
        ? initialColors(ids, definition)
        : ids.map(
            (id) =>
              state.draft.colors.find((color) => color.colorOptionId === id) ?? {
                colorOptionId: id,
                faces: {},
              },
          );
      set({
        draft: hydrate
          ? { ...state.draft, activeFaces: initialFaces(definition), colors }
          : sameIds
            ? state.draft
            : { ...state.draft, colors },
        lockedFaces:
          hydrate && !options.allowSavedFaceRemoval ? lockableFaces(definition) : state.lockedFaces,
        photoUrls: urlsChanged ? { ...state.photoUrls, ...freshUrls } : state.photoUrls,
        operations,
        notice: removed ? '삭제된 색상의 사진 편집과 업로드를 취소했습니다.' : state.notice,
      });
      if (!sameIds || hydrate) notifyChange();
    },
    setOptionalFaceEnabled: (face, enabled) => {
      const state = get();
      if (
        !canEdit() ||
        (!enabled && state.lockedFaces.includes(face)) ||
        state.draft.activeFaces.includes(face) === enabled
      )
        return;
      const activeFaces = FACE_ORDER.filter((key) =>
        key === face ? enabled : state.draft.activeFaces.includes(key),
      );
      const colors = state.draft.colors.map((color) => ({
        ...color,
        faces: Object.fromEntries(
          Object.entries(color.faces).filter(([key]) => activeFaces.includes(key as FaceKey)),
        ),
      }));
      set({
        draft: { ...state.draft, activeFaces, colors },
        dirty: true,
        operations: Object.fromEntries(
          Object.entries(state.operations).filter(([key]) => !key.endsWith(`:${face}`)),
        ),
      });
      notifyChange();
    },
    beginOperation: (colorOptionId, face, id) => {
      const key = slotKey(colorOptionId, face);
      const operation = get().operations[key];
      if (
        !canEdit() ||
        !validSlot(colorOptionId, face) ||
        (operation && operation.status !== 'error')
      )
        return false;
      set({ operations: { ...get().operations, [key]: { id, status: 'uploading' } } });
      return true;
    },
    setOperationStatus: (colorOptionId, face, id, status) => {
      if (!currentOperation(colorOptionId, face, id)) return;
      set({
        operations: { ...get().operations, [slotKey(colorOptionId, face)]: { id, status } },
      });
    },
    cancelOperation: (colorOptionId, face, id) => {
      if (!currentOperation(colorOptionId, face, id)) return;
      set({ operations: removeOperation(slotKey(colorOptionId, face)) });
      // 업로드 중 도착한 조회는 취소 후 적용한다. props 변경이 없어도 복원되어야 한다.
      const state = get();
      if (!state.dirty && Object.keys(state.operations).length === 0) {
        state.reconcile(
          state.draft.colors.map((color) => color.colorOptionId),
          latestDefinition,
        );
      }
    },
    setPhoto: ({ colorOptionId, face, operationId, photo, photoUrl }) => {
      if (!canEdit() || !currentOperation(colorOptionId, face, operationId)) return false;
      const state = get();
      const previous = state.draft.colors.find((color) => color.colorOptionId === colorOptionId)
        ?.faces[face];
      const next = photoOnly(photo);
      set({
        draft: {
          ...state.draft,
          colors: state.draft.colors.map((color) =>
            color.colorOptionId === colorOptionId
              ? { ...color, faces: { ...color.faces, [face]: next } }
              : color,
          ),
        },
        dirty: true,
        photoUrls: { ...state.photoUrls, [photo.photoKey]: photoUrl },
        operations: removeOperation(slotKey(colorOptionId, face)),
      });
      notifyChange();
      if (previous)
        options.onPhotoReplaced?.({
          productId: state.draft.productId,
          colorOptionId,
          face,
          previous,
          next,
        });
      return true;
    },
    getPhotoReadiness: () => {
      const state = get();
      const missing = state.draft.colors.flatMap((color) =>
        state.draft.activeFaces
          .filter((face) => !color.faces[face])
          .map((face) => ({ colorOptionId: color.colorOptionId, face })),
      );
      // 합성 작업은 사진 준비가 아니다 — 저장 조립이 합성 안내로 따로 막는다.
      const operations = Object.entries(state.operations)
        .filter(([key]) => !isCompositeSlot(key))
        .map(([, operation]) => operation);
      const hasErrors = operations.some((operation) => operation.status === 'error');
      const hasPending = operations.some((operation) => operation.status !== 'error');
      return {
        missing,
        hasErrors,
        hasPending,
        isPhotoStepReady:
          state.draft.colors.length > 0 && missing.length === 0 && !hasErrors && !hasPending,
      };
    },
  };
};
